/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, limit, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiCircle, FiArrowRight, FiChevronDown, FiChevronUp, FiClock } from 'react-icons/fi';

const COLLAPSE_KEY = 'nextai_onboarding_collapsed';

function GettingStarted({ projects, userId }) {
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true');
    const [hasConnection, setHasConnection] = useState(false);
    const [hasPost, setHasPost] = useState(false);

    const hasProject = projects.length > 0;
    const firstProjectId = projects[0]?.id;

    // Listen for Meta connections
    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, 'users', userId, 'connections'));
        const unsub = onSnapshot(q, (snap) => {
            setHasConnection(!snap.empty);
        }, () => {});
        return () => unsub();
    }, [userId]);

    // Listen for posts on first project
    useEffect(() => {
        if (!firstProjectId) {
            setHasPost(false);
            return;
        }
        const q = query(collection(db, 'projects', firstProjectId, 'posts'), where('status', '==', 'posted'), limit(1));
        const unsub = onSnapshot(q, (snap) => {
            setHasPost(!snap.empty);
        }, () => {});
        return () => unsub();
    }, [firstProjectId]);

    const allComplete = hasProject && hasConnection && hasPost;

    if (allComplete) return null;

    const toggleCollapse = () => {
        const next = !collapsed;
        localStorage.setItem(COLLAPSE_KEY, String(next));
        setCollapsed(next);
    };

    const steps = [
        {
            done: hasProject,
            label: 'Create your first project',
            to: '/create-project',
            disabled: false,
        },
        {
            done: hasConnection,
            label: 'Connect your Meta account',
            subtitle: hasConnection ? null : "You'll choose which Facebook Page to connect. Make sure you have one ready.",
            to: hasConnection ? null : '/settings',
            disabled: false,
        },
        {
            done: hasPost,
            label: 'Create your first post',
            to: firstProjectId ? `/project/${firstProjectId}/content-hub?workflow=single` : null,
            disabled: !hasProject,
        },
    ];

    const completedCount = steps.filter(s => s.done).length;

    return (
        <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] border border-slate-blue/20 p-6 shadow-xl">
            {/* Header row — always visible */}
            <button onClick={toggleCollapse} className="w-full flex items-center justify-between gap-4 group">
                <div className="flex items-center gap-3 min-w-0">
                    <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest">Getting Started</h2>
                    <span className="text-[10px] font-bold text-cream/30 bg-white/5 px-2 py-0.5 rounded-full">{completedCount}/{steps.length}</span>
                </div>
                <div className="flex items-center gap-3">
                    {/* Inline progress bar */}
                    <div className="hidden sm:block w-24 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-blue rounded-full transition-all duration-500" style={{ width: `${(completedCount / steps.length) * 100}%` }} />
                    </div>
                    {collapsed
                        ? <FiChevronDown className="h-4 w-4 text-cream/30 group-hover:text-cream/60 transition-colors" />
                        : <FiChevronUp className="h-4 w-4 text-cream/30 group-hover:text-cream/60 transition-colors" />
                    }
                </div>
            </button>

            {/* Expandable steps */}
            {!collapsed && (
                <div className="mt-4 space-y-2">
                    {steps.map((step, idx) => {
                        const inner = (
                            <div
                                className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-200 group/step ${
                                    step.done
                                        ? 'border-white/5 bg-white/5'
                                        : step.disabled
                                            ? 'border-white/5 bg-white/5 opacity-40 cursor-not-allowed'
                                            : step.waiting
                                                ? 'border-white/10 bg-warm-gray/20'
                                                : 'border-white/10 bg-warm-gray/20 hover:border-slate-blue/30 hover:bg-warm-gray/30'
                                }`}
                            >
                                {step.done ? (
                                    <FiCheckCircle className="h-4 w-4 text-slate-blue flex-shrink-0" />
                                ) : step.waiting ? (
                                    <FiClock className="h-4 w-4 text-cream/40 flex-shrink-0" />
                                ) : (
                                    <FiCircle className={`h-4 w-4 flex-shrink-0 ${step.disabled ? 'text-cream/20' : 'text-cream/30'}`} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-bold uppercase tracking-wide ${
                                        step.done ? 'text-cream/40 line-through decoration-cream/20' : step.disabled ? 'text-cream/30' : 'text-cream'
                                    }`}>
                                        {step.label}
                                    </p>
                                    {step.subtitle && !step.done && (
                                        <p className="text-[11px] text-cream/30 mt-0.5">{step.subtitle}</p>
                                    )}
                                </div>
                                {!step.done && !step.disabled && !step.waiting && (
                                    <FiArrowRight className="h-3.5 w-3.5 text-cream/20 group-hover/step:text-slate-blue transition-colors flex-shrink-0" />
                                )}
                            </div>
                        );

                        if (step.done || step.disabled || !step.to) {
                            return <div key={idx}>{inner}</div>;
                        }

                        return (
                            <Link key={idx} to={step.to} className="block">
                                {inner}
                            </Link>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

export default GettingStarted;
